import { useState, useEffect, useRef } from "react";
import { Upload } from "lucide-react";

// Feature: issue-image-attachment
// Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5

export const MAX_SIZE = 5 * 1024 * 1024; // 5 MiB

const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
]);

/**
 * Validates a File object against size and MIME-type rules.
 * Size is evaluated before MIME type (Requirement 4.5).
 *
 * @param {File} file
 * @returns {string|null} Error message, or null if the file is valid.
 */
export function validateFile(file) {
  if (file.size > MAX_SIZE) return "Image must be smaller than 5 MB";
  if (!ALLOWED_TYPES.has(file.type)) return "Only image files are supported";
  return null;
}

/**
 * AttachmentField — a self-contained component that handles file selection,
 * clipboard paste, drag-and-drop, thumbnail preview, and error display for
 * a single image attachment.
 *
 * Props:
 *   onAttachmentChange(file | null) — called when a valid file is selected or cleared
 *   onError(message | null)         — called when validation fails or clears
 *   reset: boolean                  — when true, clears internal preview state
 */
export default function AttachmentField({ onAttachmentChange, onError, reset }) {
  const [previewUrl, setPreviewUrl] = useState(null); // object URL for thumbnail
  const [error, setError] = useState(null);           // current validation error
  const [hasFile, setHasFile] = useState(false);      // whether a file is pending

  const fileInputRef = useRef(null);
  // Keep a ref to the current previewUrl so the unmount cleanup always
  // sees the latest value (the state closure in a [] effect would be stale).
  const previewUrlRef = useRef(null);

  // Revoke object URL on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  // React to parent-driven reset (e.g. after form submission or cancellation)
  useEffect(() => {
    if (reset) {
      clearFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  /** Clears all local state and notifies the parent. */
  function clearFile() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setError(null);
    setHasFile(false);
    onAttachmentChange(null);
    onError(null);
  }

  /**
   * Central handler for a candidate file (from picker, paste, or drop).
   * Runs validation and, if valid, creates the preview URL and notifies parent.
   */
  function handleFile(file) {
    // Dismiss any previous error when the user tries again
    if (error) {
      setError(null);
      onError(null);
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      onError(validationError);
      // Do NOT clear an existing valid file (Requirement 4.3)
      return;
    }

    // Revoke previous object URL before creating a new one
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewUrl(url);
    setHasFile(true);
    setError(null);
    onError(null);
    onAttachmentChange(file);
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  function handleInputChange(e) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset the input value so the same file can be re-selected after removal
    e.target.value = "";
  }

  function handleUploadAreaClick() {
    // Clicking the upload area also dismisses any displayed error
    if (error) {
      setError(null);
      onError(null);
    }
    fileInputRef.current?.click();
  }

  function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    // Use the first image item encountered; ignore the rest (Requirement 2.4)
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          handleFile(file);
        }
        return;
      }
    }
    // No image items — ignore (Requirement 2.5)
  }

  function handleDragOver(e) {
    e.preventDefault(); // enable drop
  }

  function handleDragEnter(e) {
    e.preventDefault();
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div data-testid="attachment-field">
      {/* Hidden file input — always present */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleInputChange}
        data-testid="attachment-file-input"
        aria-label="Upload image attachment"
      />

      {hasFile && previewUrl ? (
        // ── Preview state ────────────────────────────────────────────────
        <div
          className="flex items-start gap-3"
          data-testid="attachment-preview"
        >
          <img
            src={previewUrl}
            alt="Attachment preview"
            style={{ maxHeight: "120px", maxWidth: "240px", objectFit: "contain" }}
            className="rounded-sm border border-gray-200"
          />
          <button
            type="button"
            onClick={clearFile}
            aria-label="Remove attachment"
            data-testid="attachment-remove-btn"
            className="text-gray-400 hover:text-gray-700 transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>
      ) : (
        // ── Upload state ─────────────────────────────────────────────────
        <div
          role="button"
          tabIndex={0}
          onClick={handleUploadAreaClick}
          onKeyDown={(e) => e.key === "Enter" && handleUploadAreaClick()}
          onPaste={handlePaste}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDrop={handleDrop}
          aria-label="Screenshot / Attachment upload area"
          data-testid="attachment-upload-area"
          className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-200 rounded-sm py-4 px-3 cursor-pointer hover:border-brand-rose transition-colors text-gray-400"
        >
          {/* Upload icon */}
          <Upload className="w-5 h-5 text-gray-400" aria-hidden="true" />
          <span className="text-xs font-medium text-gray-500">
            Screenshot / Attachment
          </span>
        </div>
      )}

      {/* Inline error display */}
      {error && (
        <p
          className="mt-1 text-xs text-red-600"
          role="alert"
          data-testid="attachment-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}
